import user from '@/api/user'
import api from '@/api'
import { AppHolder } from '@/utils/app-holder'
import reflect from '@/utils/reflect-promise'
import { ADVISORY_ROLES } from '@/utils/roles'

const getBaseState = () => ({
  authenticated: false,
  email: null,
  profile: null,
  questions: null,
  plans: [],
  invitation: null,
  paymentSources: [],
  invoices: []
})

export default {
  namespaced: true,
  state: getBaseState(),

  actions: {
    fetchConfirm (context) {
      return user.fetch()
        .then(data => context.commit('fetch', data))
    },

    fetch (context, cached = true) {
      const { profile } = context.state
      if (cached && profile) {
        return Promise.resolve(profile)
      }

      return user.fetch()
        .then(data => {
          if (data.roles.find(role => ADVISORY_ROLES.indexOf(role) > -1)) {
            const accessToken = localStorage.getItem('oauthToken')
            const refreshToken = localStorage.getItem('refreshToken')
            context.commit('unauthenticate')
            AppHolder.store.commit('advisor/authenticate', {
              expiresIn: AppHolder.store.state.authExpiresAt,
              accessToken,
              refreshToken
            })
            return Promise.reject(new Error('is-admin'))
          }
          user.fetchProfile()
            .then(profile => {
              context.commit('fetch', Object.assign(data, profile.profile))
            })
            .catch(() => {
              context.commit('fetch', data)
            })
        })
    },

    fetchQuestions (context, cached = true) {
      const { questions } = context.state
      if (cached && questions) {
        return Promise.resolve(questions)
      }

      return user.fetchQuestions()
        .then(({ riskSurvey }) => context.commit('fetchQuestions', riskSurvey.filter(({type}) => type === 'profile')))
    },

    getToken (context) {
      return user.getToken()
        .then(token => token.accessToken)
    },

    sendToken (context, {token, idUser}) {
      return user.sendToken(token, idUser)
    },

    authenticate (context, credentials) {
      return user.signIn(credentials)
        .then(data => AppHolder.store.dispatch('advisor/unauthenticate')
          .then(() => context.commit('authenticate', data)))
    },

    unauthenticate (context) {
      return reflect(user.signOut())
        .then(() => context.commit('unauthenticate'))
    },

    setUnauthenticated (context) {
      context.commit('unauthenticate')
    },

    refreshToken (context) {
      return user.refreshToken()
        .then(data => context.commit('authenticate', data))
    },

    register (context, credentials) {
      return user.create(credentials)
        .then(data => context.commit('register', data))
    },

    update (context, fields) {
      return user.update(fields)
        .then(data => context.commit('update', data))
    },

    confirm (context, code) {
      return user.confirm(code)
    },

    resendConfirmation () {
      return user.resendConfirmation()
    },

    changePassword (context, fields) {
      return user.changePassword(fields)
    },

    requestResetPassword (context, email) {
      return user.requestResetPassword(email)
    },

    updateResetPassword (context, {token, password, confirmedPassword}) {
      return user.updateResetPassword({token, password, confirmedPassword})
    },

    deleteProfile () {
      return user.deleteProfile()
    },
    confirmDeleteProfile (context, token) {
      return user.confirmDeleteProfile(token)
        .then(() => context.dispatch('unauthenticate'))
        .then(AppHolder.store.dispatch('reset'))
    },

    fetchPlans (context) {
      return user.fetchPlans()
        .then(({ plans }) => context.commit('fetchPlans', plans))
    },

    verifyCoupon (context, { coupon, planId }) {
      return user.verifyCoupon(coupon, planId)
    },

    subscribe (context, data) {
      return user.subscribe(data)
    },
    fetchInvitation (context, { invitationToken, cache = true }) {
      if (cache && context.state.invitation && context.state.invitation.token === invitationToken) {
        return Promise.resolve()
      }
      return user.fetchInvitation(invitationToken)
        .then(data => context.commit('setInvitation', {
          ...data.invitation,
          token: invitationToken
        }))
    },
    markInvitationAsRead (context, invitationToken) {
      return user.markInvitationAsRead(invitationToken)
    },
    fetchPaymentSources (context, cache = true) {
      if (cache && context.state.paymentSources.length) {
        return Promise.resolve()
      }
      return user.fetchPaymentSources()
        .then(data => context.commit('setPaymentSources', data))
    },
    updatePaymentSource (context, token) {
      return user.updatePaymentSource(token)
        .then((data) => context.commit('setPaymentSources', {
          paymentSources: [data]
        }))
    },
    fetchInvoices (context, cache = true) {
      if (cache && context.state.invoices.length) {
        return Promise.resolve()
      }
      return user.fetchInvoices()
        .then(data => context.commit('setInvoices', data))
    }
  },

  mutations: {

    fetch (state, data) {
      state.profile = data
    },

    fetchQuestions (state, data) {
      state.questions = data
    },

    register (state, data) {
      state.profile = data
    },

    authenticate (state, data) {
      AppHolder.store.commit('updateToken', data.expiresIn)
      localStorage.setItem('oauthToken', data.accessToken)
      localStorage.setItem('refreshToken', data.refreshToken)
      api.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('oauthToken')}`
      state.authenticated = true
    },

    unauthenticate (state) {
      localStorage.removeItem('oauthToken')
      api.defaults.headers.common['Authorization'] = null
      state.authenticated = false
      state.email = null
      state.profile = null
    },

    update (state, data) {
      Object.keys(data).forEach(key => Object.assign(state[key], data[key]))
    },

    reset (state) {
      const baseState = getBaseState()
      Object.keys(baseState).forEach(key => {
        state[key] = baseState[key]
      })
    },

    fetchPlans (state, data) {
      state.plans = data
    },
    setInvitation (state, invitation) {
      state.invitation = invitation
    },
    setPaymentSources (state, data) {
      state.paymentSources = data.paymentSources
    },
    setInvoices (state, data) {
      state.invoices = data.invoices
    }
  },

  getters: {
    userEmail ({ email, profile }) {
      if (profile) {
        email = profile.email
      }
      return email
    },

    userName ({ profile }) {
      if (profile) {
        const {
          firstName,
          lastName,
          email
        } = profile

        return firstName ? `${firstName} ${lastName || ''}` : email
      }

      return ''
    },

    isProfileComplete ({ profile }) {
      if (profile) {
        const {
          'complete?': isComplete,
          recommendedRisk
        } = profile

        return {
          risk: recommendedRisk,
          infos: isComplete,
          full: recommendedRisk && isComplete
        }
      }

      return false
    },

    getFeatures (state, getters, rootState) {
      // If it's an advisor we override the available features
      if (rootState.advisor.authenticated && rootState.advisor.profile) {
        return rootState.advisor.profile.features
      }

      // Otherwise we get the user's available features
      if (state.profile) {
        return state.profile.features
      }

      return null
    },

    getCoupon ({ profile }) {
      if (profile) {
        const { coupon } = profile
        return coupon
      }
    }
  }
}
